import React from 'react';
import {
  Box, Container, Grid, Typography, Button, Card, CardContent,
  Stack, useTheme, alpha,
  Fade,
  Chip,
  Zoom,
  Slide,
  Grow
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  BugReport, Speed, IntegrationInstructions,
  AutoFixHigh, CheckCircle, Code,
  Refresh, TrendingUp, CloudSync,
  Sync, VerifiedUser, SmartToy
} from '@mui/icons-material';
import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';

// Clean Color Palette (Same as other pages)
const colors = {
  primary: '#4551be',
  secondary: '#6b77d4',
  success: '#10b981',
  warning: '#f59e0b',
  dark: '#1e293b',
  light: '#f0f4f8',
  textSecondary: '#64748b',
  gradient: 'linear-gradient(135deg, #4551be 0%, #6b77d4 100%)',
  border: '#e2e8f0'
};

// Subtle Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: 'white',
  borderRadius: '20px',
  border: `1px solid ${colors.border}`,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px ${alpha(colors.primary, 0.1)}`,
    borderColor: colors.primary,
    '& .service-icon': {
      transform: 'scale(1.1)',
      color: colors.primary
    }
  }
}));

const AutomationTesting = () => {
  const theme = useTheme();

  // Test Types
  const testTypes = [
    {
      id: 'functional',
      title: 'Functional Testing',
      icon: <BugReport />,
      description: 'We provide automated functional testing that validates software products against requirements using modern software tools. This replaces human-driven manual processes and brings significant gains for team efficiency.',
      color: colors.primary
    },
    {
      id: 'integration',
      title: 'Integration Testing',
      icon: <IntegrationInstructions />,
      description: 'Our integration testing solutions put ownership responsibilities in the hands of engineering teams. Test plans are developed alongside regular roadmap feature development and executed automatically through continuous integration tools.',
      color: colors.secondary
    },
    {
      id: 'automation',
      title: 'Automation Testing',
      icon: <AutoFixHigh />,
      description: 'We deliver comprehensive automation testing services using special software tools to execute complete test suites. Our solutions enter test data, compare expected and actual results and generate detailed reports without human intervention.',
      color: colors.success
    },
    {
      id: 'performance',
      title: 'Performance Testing',
      icon: <Speed />,
      description: 'We offer performance testing services that handle load, stress and scalability testing. Our approach allows developers to write code once and deploy across multiple platforms while ensuring optimal performance.',
      color: colors.warning
    },
    {
      id: 'unit',
      title: 'Unit Testing',
      icon: <Code />,
      description: 'We specialize in unit testing solutions that ensure software reliability and performance. With a focus on quality assurance, we provide comprehensive testing frameworks to streamline the development process.',
      color: colors.primary
    },
    {
      id: 'regression',
      title: 'Regression Testing',
      icon: <Refresh />,
      description: 'Our regression testing services verify that recent changes haven\'t adversely affected existing features. We handle visual regression testing and ensure software UI maintains consistency across updates.',
      color: colors.secondary
    }
  ];

  // Key Benefits
  const benefits = [
    {
      title: 'Automated Testing Integration',
      description: 'We integrate automated testing from project inception, making it a core part of modern agile and DevOps workflows.',
      icon: <Sync />
    },
    {
      title: 'Continuous Testing',
      description: 'Our solutions enable continuous testing throughout the development lifecycle, supporting rapid release cycles.',
      icon: <CloudSync />
    },
    {
      title: 'Comprehensive Coverage',
      description: 'We provide end-to-end testing solutions that cover all aspects of software quality assurance.',
      icon: <VerifiedUser />
    },
    {
      title: 'Team Efficiency',
      description: 'Our automation testing promotes lean QA team structures and allows teams to focus on critical features.',
      icon: <TrendingUp />
    }
  ];

  return (
    <Box sx={{ background: colors.light, color: colors.dark }}>
      <Navbar />

      {/* Hero Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${alpha(colors.primary, 0.03)} 0%, ${alpha(colors.secondary, 0.03)} 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    icon={<SmartToy />}
                    label="⚡ AUTOMATION TESTING EXPERTS"
                    sx={{
                      mb: 3,
                      mt: { xs: 2, md: 6 },
                      bgcolor: alpha(colors.primary, 0.1),
                      color: colors.primary,
                      fontWeight: 600,
                      border: `1px solid ${alpha(colors.primary, 0.2)}`
                    }}
                  />

                  <Typography variant="h1" sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 3,
                    color: colors.dark
                  }}>
                    Software
                    <Box component="span" sx={{ 
                      display: 'block',
                      color: colors.primary
                    }}>
                      Automation Testing
                    </Box>
                  </Typography>

                  <Typography sx={{
                    color: colors.textSecondary,
                    mb: 4,
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    maxWidth: '90%'
                  }}>
                    We provide comprehensive automation testing solutions that transform manual 
                    testing processes into efficient, reliable automated workflows. Our services 
                    help teams achieve higher quality software with improved efficiency.
                  </Typography>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{
                  animation: `${float} 6s ease-in-out infinite`,
                  textAlign: 'center'
                }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: '240px', md: '340px' },
                    height: { xs: '240px', md: '340px' },
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, ${alpha(colors.secondary, 0.1)} 100%)`,
                    margin: '0 auto',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -15,
                      left: -15,
                      right: -15,
                      bottom: -15,
                      borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.2)} 0%, ${alpha(colors.secondary, 0.2)} 100%)`,
                      zIndex: -1,
                      animation: `${float} 8s ease-in-out infinite reverse`
                    }
                  }}>
                    <SmartToy sx={{ 
                      fontSize: { xs: 100, md: 150 },
                      color: colors.primary 
                    }} />
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What We Provide */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Chip
              label="OUR TESTING SERVICES"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 2,
              color: colors.dark
            }}>
              Our Automation Testing Services
            </Typography>
            <Typography sx={{
              color: colors.textSecondary,
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1.1rem'
            }}>
              We provide specialized testing solutions tailored to modern software development needs
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testTypes.map((test, index) => (
              <Grid item xs={12} md={6} lg={4} key={test.id}>
                <Slide direction="up" in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <div>
                    <ServiceCard>
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '16px',
                          background: alpha(test.color, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          color: test.color,
                          transition: 'all 0.3s ease'
                        }} className="service-icon">
                          {test.icon}
                        </Box>

                        <Typography variant="h5" sx={{ 
                          fontWeight: 600, 
                          mb: 2,
                          color: colors.dark
                        }}>
                          {test.title}
                        </Typography>

                        <Typography sx={{ 
                          color: colors.textSecondary,
                          lineHeight: 1.6
                        }}>
                          {test.description}
                        </Typography>
                      </CardContent>
                    </ServiceCard>
                  </div>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Benefits */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${alpha(colors.primary, 0.02)} 0%, ${alpha(colors.secondary, 0.02)} 100%)`
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Chip
              label="WHY CHOOSE US"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 3,
              color: colors.dark
            }}>
              Why Choose Our Automation Testing
            </Typography>
            <Typography sx={{
              color: colors.textSecondary,
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1.1rem'
            }}>
              We deliver testing solutions that align with modern development practices and business goals
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Grow in timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
                  <Box sx={{
                    background: 'white',
                    borderRadius: '16px',
                    p: 4,
                    height: '100%',
                    border: `1px solid ${colors.border}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: colors.primary,
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 24px ${alpha(colors.primary, 0.1)}`
                    }
                  }}>
                    <Stack direction="row" spacing={3}>
                      <Box sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '12px',
                        background: alpha(colors.primary, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: colors.primary,
                        flexShrink: 0
                      }}>
                        {benefit.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600,
                          mb: 2,
                          color: colors.dark
                        }}>
                          {benefit.title}
                        </Typography>
                        <Typography sx={{ 
                          color: colors.textSecondary,
                          lineHeight: 1.6
                        }}>
                          {benefit.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Detailed Service Information */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Typography variant="h2" sx={{
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 700,
                    mb: 4,
                    color: colors.dark
                  }}>
                    Our Approach to Automation Testing
                  </Typography>

                  <Stack spacing={4}>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600,
                        mb: 2,
                        color: colors.primary
                      }}>
                        For Frequent Releases
                      </Typography>
                      <Typography sx={{ 
                        color: colors.textSecondary,
                        lineHeight: 1.6
                      }}>
                        We provide automation testing solutions that are essential for software products 
                        with rapid release cycles. Our testing frameworks integrate seamlessly with 
                        continuous integration and delivery pipelines, ensuring quality at every stage 
                        of development.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600,
                        mb: 2,
                        color: colors.primary
                      }}>
                        Customized Testing Strategies
                      </Typography>
                      <Typography sx={{ 
                        color: colors.textSecondary,
                        lineHeight: 1.6
                      }}>
                        We understand that there is no one-size-fits-all solution for automated testing. 
                        Our team works with you to develop customized testing strategies that align with 
                        your specific requirements, technology stack and business objectives.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600,
                        mb: 2,
                        color: colors.primary
                      }}>
                        Focus on Critical Features
                      </Typography>
                      <Typography sx={{ 
                        color: colors.textSecondary,
                        lineHeight: 1.6
                      }}>
                        Our automation testing enables your QA team to focus on more sensitive and 
                        complex features, while routine tests are handled automatically. This approach 
                        promotes lean QA team structures and maximizes testing effectiveness.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{
                  background: 'white',
                  borderRadius: '20px',
                  border: `1px solid ${colors.border}`,
                  p: 4,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
                }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 600,
                    mb: 3,
                    color: colors.dark
                  }}>
                    What We Deliver
                  </Typography>

                  <Stack spacing={3}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircle sx={{ color: colors.success, mt: '4px' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 0.5, color: colors.dark }}>
                          Automated Test Execution
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                          We provide tools that automatically execute test suites, enter test data, 
                          and compare results without manual intervention
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircle sx={{ color: colors.success, mt: '4px' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 0.5, color: colors.dark }}>
                          Detailed Test Reporting
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                          Our solutions generate comprehensive test reports that provide clear insights 
                          into software quality and testing coverage
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircle sx={{ color: colors.success, mt: '4px' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 0.5, color: colors.dark }}>
                          Regression Testing
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                          We ensure that new changes don't break existing functionality through 
                          comprehensive regression testing
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircle sx={{ color: colors.success, mt: '4px' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 0.5, color: colors.dark }}>
                          Continuous Testing Integration
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                          Our testing solutions integrate with your CI/CD pipeline for continuous 
                          testing throughout the development lifecycle
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        py: { xs: 10, md: 15 },
        background: colors.gradient,
        color: '#ffffff'
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 700,
              mb: 3,
              color: '#ffffff'
            }}>
              Ready to Automate Your Testing?
            </Typography>

            <Typography sx={{
              maxWidth: '800px',
              mx: 'auto',
              mb: 6,
              fontSize: '1.2rem',
              lineHeight: 1.6,
              opacity: 0.9
            }}>
              Contact us to learn how our automation testing services can improve your software
              quality, accelerate releases and optimize your testing processes
            </Typography>
          </Box>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default AutomationTesting;